import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { db } from '@vibe/db/server';
import { executeIntegration } from '@api/integration-block.server';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';
import { pressLetters } from '@generated/models';
import { PublicRelationsListBoard } from '@api/BoardSDK';

const GMAIL = 'c70cb228-a141-49d4-a586-364172111780';
const GEORGE = 'gbrook8344@gmail.com';
const PORTAL = 'https://bit.ly/star-revue';
const MEDIA_KIT_PDF = 'https://files-public.monday.com/use1/c2888573-f8da-4b81-9c93-fd6e3fef0a8f/Star-Revue-Media-Kit.pdf';

/** Press version of the Nov 2 agency letter — starting draft for George to edit. */
const LETTER_1 = {
  subject: 'Pitch: Two Brooklyn and Manhattan print papers built their own self-service ad portal',
  body: `Hi {first},

I'm George Fiala, publisher of the Red Hook Star-Revue (Brooklyn) and The Village Star-Revue (Manhattan) - two award-winning monthly community newspapers that are still in print, still growing, and read cover to cover by their neighborhoods.

While local papers keep closing, we've gone the other way. This fall we launched our own self-service portal, where local businesses can see our rates, sizes and specials, book space and upload their ad in minutes - or get free help designing one. A two-paper independent operation, competing on the same footing as the big platforms.

You can see it here:
${PORTAL}

Our new media kit (PDF) tells the rest of the story - who reads us, how we cover downtown Brooklyn and the East and West Village, and why print still works here:
${MEDIA_KIT_PDF}

We publish 4,000 copies of each paper every month, and every print ad also runs online.

I think there's a story here about what keeps a small local paper alive in 2026, and I'd be glad to talk, show you around the portal, or send copies of the papers.

Thanks for reading,
George Fiala
Publisher, Red Hook Star-Revue & The Village Star-Revue
${GEORGE}
917 652-9128`,
};

/** PR rule: every press letter's subject includes "Pitch". */
export const withPitch = (s: string) => (/\bpitch\b/i.test(s) ? s : `Pitch: ${s}`);
/** Gmail rejects some letters with runs of spaces / extra blank lines; every press send goes through this. */
export const tidyBody = (s: string) => s.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
export const fillFirst = (body: string, contact: string | null | undefined) => {
  const first = contact?.trim().split(/\s+/)[0] ?? '';
  return body.replace(/\{first\}/g, first && !/^(general|editors?|letters|media|ideas)$/i.test(first) ? first : 'there');
};
const ascii = (s: string) => s.normalize('NFKD').replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"').replace(/[\u2013\u2014]/g, '-').replace(/\u2026/g, '...').replace(/[^\x09\x0a\x0d\x20-\x7e]/g, '');

async function all() {
  const col = db.collection(pressLetters);
  let docs = await col.orderBy('number', 'asc').limit(100).get();
  if (!docs.length) {
    const now = new Date().toISOString();
    await col.add({ number: 1, ...LETTER_1, createdAt: now, updatedAt: now });
    docs = await col.orderBy('number', 'asc').limit(100).get();
  }
  return docs;
}

export const getPressLetters = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return (await all()).map((d) => ({ id: d.id, ...d.data }));
  });

export const savePressLetter = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().optional(), subject: z.string().trim().min(1).max(300), body: z.string().min(1).max(10000) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const col = db.collection(pressLetters);
    const now = new Date().toISOString();
    const subject = withPitch(data.subject);
    if (data.id) { await col.doc(data.id).update({ subject, body: data.body, updatedAt: now }); return { ok: true, subject }; }
    const number = Math.max(0, ...(await all()).map((d) => d.data.number)) + 1;
    await col.add({ number, subject, body: data.body, createdAt: now, updatedAt: now });
    return { ok: true, subject, number };
  });

const nyDate = () => new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + 'T12:00:00Z');

/** Everyone still "Not Contacted" with an email (not removed), one per address. */
export const getPressRecipients = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const b = new PublicRelationsListBoard();
    const rows: { id: string; outlet: string; contact: string | null; email: string }[] = [];
    let cursor: string | null | undefined;
    let other = 0, noEmail = 0;
    const seen = new Set<string>();
    do {
      const r = await withBoardAuth(() => b.items().withColumns(['contactName', 'email', 'outreachStatus', 'removed']).withPagination({ limit: 100, ...(cursor ? { cursor } : {}) }).execute());
      for (const i of r.items) {
        if (i.removed) continue;
        const e = i.email?.email?.trim().toLowerCase();
        if (!e) { noEmail++; continue; }
        if ((i.outreachStatus ?? 'Not Contacted') !== 'Not Contacted') { other++; continue; }
        if (seen.has(e)) continue;
        seen.add(e);
        rows.push({ id: i.id, outlet: i.name, contact: i.contactName, email: e });
      }
      cursor = r.cursor;
    } while (cursor);
    rows.sort((a, b2) => a.outlet.localeCompare(b2.outlet));
    return { rows, other, noEmail };
  });

/** Sends one letter to one PR row, then marks it "Letter Sent" with today's NY date. */
export const sendPressLetterTo = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), letterNumber: z.number().int().min(1), rowId: z.string().regex(/^\d+$/), force: z.boolean().optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const letter = (await all()).find((d) => d.data.number === data.letterNumber)?.data;
    if (!letter) throw new Error('Letter not found');
    const b = new PublicRelationsListBoard();
    const row = await withBoardAuth(() => b.item(data.rowId).withColumns(['contactName', 'email', 'outreachStatus']).execute());
    const to = row?.email?.email?.trim();
    if (!row || !to) return { status: 'skipped' as const };
    if (!data.force && (row.outreachStatus ?? 'Not Contacted') !== 'Not Contacted') return { status: 'skipped' as const };
    const subj = ascii(withPitch(letter.subject)).replace(/[[\]{}<>]/g, '').trim();
    const body = tidyBody(ascii(fillFirst(letter.body, row.contactName)));
    const subjects = [subj, subj.replace(/:/g, ' -').replace(/[^A-Za-z0-9 ,.'-]/g, '').replace(/\s+/g, ' ').trim()];
    let lastErr: unknown;
    for (const subject of subjects) {
      try {
        await withBoardAuth(() => executeIntegration(GMAIL, { to: [to], subject, body }));
        await withBoardAuth(() => b.item(data.rowId).update({ outreachStatus: 'Letter Sent', lastContacted: nyDate() }).execute());
        return { status: 'sent' as const };
      } catch (e) {
        console.error('press send failed', data.rowId, e); lastErr = e;
        if ((e as { code?: string })?.code === 'AI_CREDITS_EXHAUSTED') throw new Error('CREDITS: Your monday AI credits have run out for now, so emails from the app cannot be sent.');
        await new Promise((r) => setTimeout(r, 3000));
      }
    }
    void lastErr;
    return { status: 'failed' as const };
  });

/** Sends the draft to George only, marked SAMPLE. */
export const samplePressLetter = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), subject: z.string().min(1).max(300), body: z.string().min(1).max(10000), to: z.email().optional(), toName: z.string().max(60).optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const subj = ascii(`SAMPLE - ${withPitch(data.subject)}`).replace(/[[\]{}<>]/g, '');
    const full = ascii(fillFirst(data.body, data.toName ?? 'George'));
    const plainSubj = subj.replace(/:/g, ' -').replace(/[^A-Za-z0-9 ,.'-]/g, '').replace(/\s+/g, ' ').trim();
    // Gmail answers "invalid field values" for some content. Step down until it accepts, and log which step worked.
    // Gmail refused a letter with double spaces; tidied spacing is accepted — so always tidy (saved text is untouched).
    const tidy = tidyBody(full);
    const tries: [string, string, string][] = [
      ['as written', subj, tidy],
      ['plain subject', plainSubj, tidy],
      ['links unlinked', plainSubj, full.replace(/[ \t]{2,}/g, ' ').replace(/https?:\/\//g, '').trim()],
    ];
    let lastErr: unknown;
    for (const [label, subject, body] of tries) {
      try {
        await withBoardAuth(() => executeIntegration(GMAIL, { to: [data.to ?? GEORGE], subject, body }));
        console.log('press sample sent:', label);
        return { ok: true, how: label };
      } catch (e) {
        console.error('press sample failed:', label, `subject=${JSON.stringify(subject)}`, `bodyLength=${body.length}`, e); lastErr = e;
        if ((e as { code?: string })?.code === 'AI_CREDITS_EXHAUSTED') throw new Error('CREDITS: Your monday AI credits have run out for now, so emails from the app cannot be sent.');
        await new Promise((r) => setTimeout(r, 2500));
      }
    }
    // Diagnostic: does Gmail accept anything right now?
    try {
      await withBoardAuth(() => executeIntegration(GMAIL, { to: [data.to ?? GEORGE], subject: 'SAMPLE test from the PR List', body: 'This is a short test. Gmail refused the full pitch letter text, so George knows the connection itself works.' }));
      console.log('press sample: short test accepted, letter text rejected');
      throw new Error('CREDITS: Gmail refused the letter text, but a short test email did go through - check your inbox. Something in the wording is being rejected.');
    } catch (e) {
      if (e instanceof Error && e.message.startsWith('CREDITS:')) throw e;
      console.error('press sample: short test also failed', e);
    }
    throw lastErr;
  });
